// src/pages/Tour/StartKm.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import tourService from '../../services/tour.service';
import Toast from '../../components/Toast/Toast';
import './TourPages.css';

const TRANSPORT_OPTIONS = [
  { value: 'BIKE',             label: '🏍️ Bike' },
  { value: 'CAR',              label: '🚗 Car' },
  { value: 'TAXI',             label: '🚕 Taxi' },
  { value: 'PUBLIC_TRANSPORT', label: '🚌 Public Transport' },
];

const TOUR_TYPE_OPTIONS = [
  { value: 'WORKING',     label: 'Working' },
  { value: 'LEAVE',       label: 'Leave' },
  { value: 'CAMPAIGNING', label: 'Campaigning' },
];

const StartKm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [todayTour,     setTodayTour]     = useState(null);
  const [checkingToday, setCheckingToday] = useState(true);
  const [employees,     setEmployees]     = useState([]);
  const [toast,         setToast]         = useState(null);
  const [isSubmitting,  setIsSubmitting]  = useState(false);
  const [errors,        setErrors]        = useState({});

  const [formData, setFormData] = useState({
    tourDate:         new Date().toISOString().split('T')[0],
    tourType:         'WORKING',
    startKm:          '',
    startLatitude:    '',
    startLongitude:   '',
    startDestination: '',
    modeOfTransport:  '',
    travelingWith:    '',
  });

  useEffect(() => {
    const checkToday = async () => {
      try {
        const res = await tourService.getTodayTour();
        if (res.success && res.data) setTodayTour(res.data);
      } catch (_) {}
      finally { setCheckingToday(false); }
    };
    checkToday();
  }, []);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const res = await tourService.getTravelingWithEmployees();
        if (res.success && res.data) setEmployees(res.data);
      } catch (_) {}
    };
    fetchEmployees();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const detectLocation = () => {
  if (!navigator.geolocation) {
    setToast({ message: 'Geolocation is not supported by your browser.', type: 'error' });
    return;
  }

  setToast({ message: 'Detecting location…', type: 'info' });

  let bestReading = null;
  let settled = false;

  const finish = (position) => {
  if (settled) return;
  settled = true;
  navigator.geolocation.clearWatch(watchId);

  const { latitude, longitude, accuracy } = position.coords;
  setFormData(prev => ({
    ...prev,
    startLatitude: latitude.toFixed(8),      // was: latitude
    startLongitude: longitude.toFixed(8),    // was: longitude
    startLocationAccuracy: accuracy,         // was: locationAccuracy
  }));

  setToast({ message: '📍 Location detected!', type: 'success' });
};

  const watchId = navigator.geolocation.watchPosition(
    (position) => {
      if (!bestReading || position.coords.accuracy < bestReading.coords.accuracy) {
        bestReading = position;
      }
      // good enough — stop early
      if (position.coords.accuracy <= 15) {
        finish(position);
      }
    },
    (error) => {
      if (settled) return;
      settled = true;
      navigator.geolocation.clearWatch(watchId);
      const message =
        error.code === error.PERMISSION_DENIED
          ? 'Location access denied. Please enable it in your browser/app settings.'
          : error.code === error.TIMEOUT
          ? 'Location request timed out. Please try again.'
          : 'Unable to detect location. ';
      setToast({ message, type: 'error' });
      console.error('Geolocation error:', error);
    },
    { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
  );

  // Hard stop after ~12s — use best reading we've got rather than hang forever
  setTimeout(() => {
    if (!settled && bestReading) {
      finish(bestReading);
    } else if (!settled) {
      settled = true;
      navigator.geolocation.clearWatch(watchId);
      setToast({ message: 'Could not get an accurate location. ', type: 'error' });
    }
  }, 12000);
};

  const validate = () => {
    const newErrors = {};
    if (!formData.tourDate) newErrors.tourDate = 'Date is required';
    if (!formData.tourType) newErrors.tourType = 'Tour type is required';

    if (formData.tourType === 'WORKING') {
      if (formData.startKm === '' || formData.startKm === null || formData.startKm === undefined)
        newErrors.startKm = 'Start KM is required';
      else if (isNaN(formData.startKm) || parseInt(formData.startKm) < 0)
        newErrors.startKm = 'Enter a valid KM value';
      if (!formData.modeOfTransport)
        newErrors.modeOfTransport = 'Mode of transport is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const payload = {
        tourDate: formData.tourDate,
        tourType: formData.tourType,
      };

      if (formData.tourType === 'WORKING') {
        payload.startKm          = parseInt(formData.startKm);
        payload.startLatitude    = formData.startLatitude    || undefined;
        payload.startLongitude   = formData.startLongitude   || undefined;
        payload.startDestination = formData.startDestination || undefined;
        payload.modeOfTransport  = formData.modeOfTransport;
        payload.travelingWith    = formData.travelingWith
          ? parseInt(formData.travelingWith)
          : undefined;
      }

      const tourRes = await tourService.startKm(payload);

      if (!tourRes.success) {
        setToast({ message: 'Failed to start tour', type: 'error' });
        return;
      }

      setToast({ message: 'Tour started successfully!', type: 'success' });
      setTimeout(() => navigate('/tour/monthly'), 1500);
    } catch (error) {
      setToast({
        message: error.response?.data?.message || 'Failed to submit Start KM',
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isWorkingDay = formData.tourType === 'WORKING';

  if (checkingToday) {
    return (
      <div className="tour-page-container">
        <div className="tour-loading">Checking today's tour status...</div>
      </div>
    );
  }

  return (
    <div className="tour-page-container">
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      <nav className="tour-breadcrumb">
        <a href="/dashboard/overview">Dashboard</a>
        <span>›</span><span>Tour</span>
        <span>›</span><span>Start KM</span>
      </nav>

      <div className="tour-header">
        <h4 className="tour-title">Start KM</h4>
        <p className="tour-subtitle">Submit your tour start details for the day</p>
      </div>

      {todayTour ? (
        <div className="tour-locked-card">
          <div className="tour-locked-icon">🔒</div>
          <h5 className="tour-locked-title">Start KM Already Submitted</h5>
          <p className="tour-locked-text">
            You have already submitted Start KM for today. Once submitted, it cannot be edited.
          </p>
          <div className="tour-locked-detail">
            <div className="tour-detail-row">
              <span className="tour-detail-label">Tour Date</span>
              <span className="tour-detail-value">
                {new Date(todayTour.tourDate).toLocaleDateString('en-IN', {
                  day: '2-digit', month: 'short', year: 'numeric',
                })}
              </span>
            </div>
            <div className="tour-detail-row">
              <span className="tour-detail-label">Tour Type</span>
              <span className="tour-detail-value">{todayTour.tourType}</span>
            </div>
            {todayTour.startKm !== undefined && todayTour.startKm !== null && (
              <div className="tour-detail-row">
                <span className="tour-detail-label">Start KM</span>
                <span className="tour-detail-value">{todayTour.startKm} km</span>
              </div>
            )}
            {todayTour.modeOfTransport && (
              <div className="tour-detail-row">
                <span className="tour-detail-label">Transport</span>
                <span className="tour-detail-value">
                  {todayTour.modeOfTransport.replace('_', ' ')}
                </span>
              </div>
            )}
            <div className="tour-detail-row">
              <span className="tour-detail-label">Status</span>
              <span className="tour-detail-value">{todayTour.status}</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="tour-form-card">
          <p className="form-section-title">Tour Details</p>
          <form onSubmit={handleSubmit}>

            {/* Row 1: Date + Tour Type */}
            <div className="tour-form-row">
              <div className="tour-form-group">
                <label className="tour-label">
                  Tour Date <span className="required">*</span>
                </label>
                <input
                  type="date"
                  name="tourDate"
                  className={`tour-input ${errors.tourDate ? 'error' : ''}`}
                  value={formData.tourDate}
                  onChange={handleChange}
                  max={new Date().toISOString().split('T')[0]}
                />
                {errors.tourDate && (
                  <span className="tour-error-message">{errors.tourDate}</span>
                )}
              </div>

              <div className="tour-form-group">
                <label className="tour-label">
                  Tour Type <span className="required">*</span>
                </label>
                <select
                  name="tourType"
                  className={`tour-select ${errors.tourType ? 'error' : ''}`}
                  value={formData.tourType}
                  onChange={handleChange}
                >
                  {TOUR_TYPE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {errors.tourType && (
                  <span className="tour-error-message">{errors.tourType}</span>
                )}
              </div>
            </div>

            {isWorkingDay && (
              <>
                {/* Row 2: Start KM + Mode of Transport */}
                <div className="tour-form-row">
                  <div className="tour-form-group">
                    <label className="tour-label">
                      Start KM <span className="required">*</span>
                    </label>
                    <input
                      type="number"
                      name="startKm"
                      className={`tour-input ${errors.startKm ? 'error' : ''}`}
                      value={formData.startKm}
                      onChange={handleChange}
                      placeholder="Enter starting kilometer"
                      min="0"
                    />
                    {errors.startKm && (
                      <span className="tour-error-message">{errors.startKm}</span>
                    )}
                  </div>

                  <div className="tour-form-group">
                    <label className="tour-label">
                      Mode of Transport <span className="required">*</span>
                    </label>
                    <select
                      name="modeOfTransport"
                      className={`tour-select ${errors.modeOfTransport ? 'error' : ''}`}
                      value={formData.modeOfTransport}
                      onChange={handleChange}
                    >
                      <option value="">Select Mode of Transport</option>
                      {TRANSPORT_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    {errors.modeOfTransport && (
                      <span className="tour-error-message">{errors.modeOfTransport}</span>
                    )}
                  </div>
                </div>

                {/* Row 3: Traveling With + Destination */}
                <div className="tour-form-row">
                  <div className="tour-form-group">
                    <label className="tour-label">Traveling With</label>
                    <select
                      name="travelingWith"
                      className="tour-select"
                      value={formData.travelingWith}
                      onChange={handleChange}
                    >
                      <option value="">Self</option>
                      {employees.map(emp => (
                        <option key={emp.id} value={emp.id}>
                          {emp.firstName} {emp.lastName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="tour-form-group">
                    <label className="tour-label">Destination</label>
                    <input
                      type="text"
                      name="startDestination"
                      className="tour-input"
                      value={formData.startDestination}
                      onChange={handleChange}
                      placeholder="Enter destination"
                    />
                  </div>
                </div>

                {/* Row 4: Location */}
                <div className="tour-form-row">
                  <div className="tour-form-group">
                    <label className="tour-label">Latitude <span className="required">*</span></label>
                    <div className="location-input-group">
                      <input
                        type="text"
                        name="startLatitude"
                        className="tour-input"
                        value={formData.startLatitude}
                        onChange={handleChange}
                        placeholder="Enter latitude"
                        required
                      />
                      <button
                        type="button"
                        className="btn-detect"
                        onClick={detectLocation}
                      >
                        📍 Detect
                      </button>
                    </div>
                  </div>
                  <div className="tour-form-group">
                    <label className="tour-label">Longitude <span className="required">*</span></label>
                    <input
                      type="text"
                      name="startLongitude"
                      className="tour-input"
                      value={formData.startLongitude}
                      onChange={handleChange}
                      placeholder="Enter longitude"
                      required
                    />
                  </div>
                </div>
              </>
            )}

            {/* Actions */}
            <div className="tour-form-actions">
              <button
                type="button"
                className="btn-tour-secondary"
                onClick={() => navigate('/tour/monthly')}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-tour-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Start KM'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default StartKm;